import {
  FISH_BITE_MIN_MS, FISH_BITE_MAX_MS, FISH_CATCH_FULL, FISH_CATCH_START,
  FISH_GAME, WATER_PROFILES, WATER_FISH, rollFish, fishZoneWidth, FISH_HAT_ZONE_BONUS, type FishKind, type WaterProfile,
} from "@ghostland/shared";
import { fishDebug } from "../ui/fishingDebug";

/**
 * Client-side fishing minigame — a first-person overlay drawn over the 3D view. The world enters a
 * first-person fishing camera (see world3d) and hands the controller frame ticks + whether the player
 * is "reeling" (holding the mouse / key). The controller runs the whole catch on the client and only
 * reports the OUTCOME up to the world, which authorises it with the server (Fish message).
 *
 * Flow: cast → WAIT a random time (≤ 2 min) for a bite → the line tugs and a catch BAR appears →
 * hold to reel (the bar rises), release and it falls; the hooked fish periodically LUNGES (the bar is
 * dragged down even while reeling). Fill the bar to full green to LAND the fish; let it empty (or run
 * out the fight timer) and the fish escapes.
 *
 * The fish that bites is rolled from the player's fishing level, so harder fish only appear (and fight
 * harder) as you level up — mirroring mining's tier gate.
 */

type Phase = "casting" | "waiting" | "biting" | "done";
export type FishEvent =
  | { type: "bite" }                       // the line just tugged — caller may play a sound
  | { type: "caught"; kind: FishKind }     // landed it — caller sends Fish + exits
  | { type: "escaped" };                   // it got away — caller exits

/** Hard cap on the reel fight once a fish is hooked — run it out and the fish escapes. */
const FIGHT_TIMEOUT_MS = 30000;
/** The cast fly-out animation (line thrown from the rod tip to the water) before the wait. */
const CAST_MS = 700;
/** RADAR minigame tuning: green-zone width (track fraction), steer/drift speeds (track/sec),
 *  and how fast the success bar moves while the fish is inside / outside the zone. */

const ZONE_PUSH = 2.4;    // A / D steering FORCE (the zone has velocity + drag now)
const SUCCESS_GAIN = 11;  // per second while the fish is inside the green zone
const SUCCESS_LOSS = 8;   // per second while it isn't

export class FishingController {
  private phase: Phase | null = null;
  private level = 1;
  private fish: FishKind = "mudfish";
  private biteAt = 0;       // performance.now() when the fish will bite
  private hookedAt = 0;     // when the fish bit (fight timer origin)
  private bar = 0;          // 0..FISH_CATCH_FULL
  private lastTick = 0;
  private startedAt = 0;
  private nextLungeAt = 0;
  private lungeUntil = 0;

  private water: WaterProfile = WATER_PROFILES.pond; // current profile of the water being fished

  private kinds: readonly FishKind[] = WATER_FISH.lake; // which fish live in the water being fished
  private fishPos = 0.5;    // fish icon position on the radar track (0..1)
  private fishVel = 0;      // fish velocity (track units/s) — it accelerates, never teleports
  private fishTarget = 0.5; // where its current swim is headed
  private nextDartAt = 0;   // when it picks the next swim target
  private zoneX = 0.5;      // green catch-zone centre (0..1)
  private zoneW = 0.1;      // zone width — scales with fishing level (min Lv1 → max Lv60)
  private hatBonus = 0;     // extra zone width from the worn Fishing Hat (FISH_HAT_ZONE_BONUS)
  private speedMult = 1;    // fish-speed multiplier (Fishing Hat slows fish to FISH_HAT_SPEED_MULT)
  private zoneVel = 0;      // zone velocity (track units/s) — forces push it, drag slows it
  private zoneForce = 0;    // current smoothed "water gust" force on the zone
  private gustTarget = 0;   // the force the gust is easing toward
  private nextGustAt = 0;   // when the water picks a new gust direction/strength

  private castStart = 0;    // when the cast fly-out animation began
  private castPower = 1;     // 0..1 throw power — sets how far the bobber lands
  private bobFracX = 0.46;   // bobber landing spot (fractions of the canvas)
  private surfFracY = 0.58;

  private readonly overlay: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly hint: HTMLDivElement;

  constructor() {
    this.overlay = document.createElement("div");
    this.overlay.id = "gl-fishing";
    this.overlay.style.cssText =
      "position:fixed;inset:0;z-index:60;display:none;pointer-events:none;";
    this.canvas = document.createElement("canvas");
    this.canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;image-rendering:pixelated;";
    this.hint = document.createElement("div");
    this.hint.style.cssText =
      "position:absolute;left:0;right:0;bottom:27%;text-align:center;color:#dfe8ee;" +
      "font:600 15px ui-monospace,monospace;letter-spacing:.5px;text-shadow:0 1px 3px #000;";
    this.overlay.appendChild(this.canvas);
    this.overlay.appendChild(this.hint);
    document.body.appendChild(this.overlay);
    window.addEventListener("resize", this.resize);
  }

  isFishing(): boolean { return this.phase != null; }
  /** A fish is ON THE HOOK right now (quitting here counts as it slipping away — bait is lost). */
  isHooked(): boolean { return this.phase === "biting"; }

  // ── CAST-CHARGE bar (3rd person, while E is held): a meter that runs 0 → max → 0 — release at
  // the top for the longest throw. The world computes the power; this just renders it. ──────────
  private chargeEl: HTMLDivElement | null = null;
  private chargeFill: HTMLDivElement | null = null;

  chargeShow(): void {
    if (!this.chargeEl) {
      const el = document.createElement("div");
      el.style.cssText =
        "position:fixed;left:50%;bottom:96px;transform:translateX(-50%);z-index:60;width:240px;height:16px;" +
        "background:#0a0a0a;border:2px solid #f4f4f4;box-shadow:0 0 0 2px #000;image-rendering:pixelated;";
      const fill = document.createElement("div");
      fill.style.cssText = "height:100%;width:0%;background:#7ce0a0;";
      el.appendChild(fill);
      document.body.appendChild(el);
      this.chargeEl = el; this.chargeFill = fill;
    }
    this.chargeEl.style.display = "block";
  }

  chargeUpdate(power: number): void {
    if (!this.chargeFill) return;
    const p = Math.max(0, Math.min(1, power));
    this.chargeFill.style.width = `${Math.round(p * 100)}%`;
    this.chargeFill.style.background = p > 0.85 ? "#ffd23f" : "#7ce0a0"; // gold at the sweet spot
  }

  chargeHide(): void {
    if (this.chargeEl) this.chargeEl.style.display = "none";
  }

  /** Begin a cast: first a short CASTING animation (the line flies from the rod tip out to the
   *  water — how far depends on `power`), then the wait. Picks a random bite time (≤ 2 min). */
  start(level: number, nowMs: number, power = 1, water: WaterProfile = WATER_PROFILES.pond, kinds: readonly FishKind[] = WATER_FISH.lake, hatBonus = 0, speedMult = 1): void {
    this.water = water;
    this.kinds = kinds;
    this.hatBonus = hatBonus; // Fishing Hat widens the green zone
    this.speedMult = speedMult; // …and slows the fish
    this.zoneW = fishZoneWidth(Math.max(1, Math.floor(level)), fishDebug.zoneMin, fishDebug.zoneMax) + this.hatBonus;
    this.phase = "casting";
    this.level = Math.max(1, Math.floor(level));
    this.startedAt = nowMs;
    this.lastTick = nowMs;
    this.castStart = nowMs;
    this.castPower = Math.max(0, Math.min(1, power));
    // The bobber's landing spot scales with throw power: harder = farther out (higher + lefter).
    // The bait lands CLOSE to the rod head — power still pushes it a little farther out.
    this.bobFracX = 0.56 - 0.05 * this.castPower;
    this.surfFracY = 0.56 - 0.06 * this.castPower;
    // bias toward shorter waits (rnd²) while still allowing the rare ~2-minute wait
    const r = Math.random();
    this.biteAt = fishDebug.enabled
      ? nowMs + CAST_MS // DEBUG: the fish bites the moment the line lands
      : nowMs + CAST_MS + FISH_BITE_MIN_MS + r * r * (FISH_BITE_MAX_MS - FISH_BITE_MIN_MS);
    this.bar = 0;
    this.resize();
    this.overlay.style.display = "block";
    this.hint.textContent = "";
  }

  /** Stop fishing immediately (cancel / walked away / done) and hide the overlay. */
  cancel(): void {
    this.phase = null;
    this.overlay.style.display = "none";
  }

  /**
   * Per-frame update. `steer` is the A/D input (-1 left, 0, +1 right) driving the green zone.
   * Returns a FishEvent the world should act on, or null. On "caught"/"escaped" it has stopped.
   */
  tick(nowMs: number, steer: number): FishEvent | null {
    if (!this.phase) return null;
    const dt = Math.min(0.05, Math.max(0, (nowMs - this.lastTick) / 1000));
    this.lastTick = nowMs;

    if (this.phase === "casting") {
      const t = Math.min(1, (nowMs - this.castStart) / CAST_MS);
      this.draw(nowMs, false, t);
      if (t >= 1) { this.phase = "waiting"; this.hint.textContent = "Waiting for a bite…"; }
      return null;
    }

    if (this.phase === "waiting") {
      this.draw(nowMs);
      if (nowMs >= this.biteAt) {
        // a fish bites — roll which one (gated by level) and open the catch bar.
        // DEBUG panel can force a specific kind (server still validates the level on catch).
        this.fish = (fishDebug.enabled && fishDebug.forceKind) || rollFish(this.level, Math.random(), this.kinds);
        this.phase = "biting";
        this.hookedAt = nowMs;
        this.bar = FISH_CATCH_START;   // the success bar starts in the middle
        this.fishPos = 0.5; this.fishTarget = 0.5; this.zoneX = 0.5;
        this.fishVel = 0; this.zoneVel = 0; this.zoneForce = 0; this.gustTarget = 0; this.nextGustAt = nowMs;
        this.nextDartAt = nowMs;
        this.hint.textContent = "Fish on! Keep it in the green zone — steer with A / D";
        return { type: "bite" };
      }
      return null;
    }

    if (this.phase === "biting") {
      const g = FISH_GAME[this.fish];
      // zone width grows with fishing level; the debug panel can retune min/max live
      this.zoneW = fishZoneWidth(this.level, fishDebug.zoneMin, fishDebug.zoneMax) + this.hatBonus;
      // THE FISH: always swimming, with real velocity. It picks a target (how FAR scales with its
      // energy), then ACCELERATES toward it and brakes into arrival — so it flows slow → fast →
      // slow, never teleports. How hard it accelerates and its top speed scale with its speed.
      const dfx = this.fishTarget - this.fishPos;
      if (nowMs >= this.nextDartAt || Math.abs(dfx) < 0.01) {
        // swim distance scales hard with energy: a pondfish nudges along, a koi tears across
        const jump = (Math.random() * 2 - 1) * (0.06 + 0.75 * g.energy);
        this.fishTarget = Math.max(0.03, Math.min(0.97, this.fishPos + jump));
        // retarget cadence scales with energy too: lazy fish cruise long calm stretches,
        // wild fish change their mind constantly
        this.nextDartAt = nowMs + 250 + Math.random() * 350 + 2200 * Math.pow(1 - g.energy, 1.5);
      }
      // tiny baselines — the speed factor DOMINATES, so tiers feel wildly different
      const spd = g.speed * this.speedMult;                  // Fishing Hat slows the fish
      const maxV = 0.06 + 1.10 * spd;                        // top speed (track/s)
      const accel = 0.25 + 4.5 * spd;                        // how hard it can accelerate
      const desiredV = Math.max(-maxV, Math.min(maxV, dfx * 5)); // brake smoothly into the target
      this.fishVel += Math.max(-accel * dt, Math.min(accel * dt, desiredV - this.fishVel));
      this.fishPos = Math.max(0.03, Math.min(0.97, this.fishPos + this.fishVel * dt));

      // THE GREEN ZONE: a physical float. A / D apply steering force; the water applies a steady
      // current (dir × flow) plus GUSTS — a smoothed random force that leans right for a while,
      // then left — whose strength is the water's chaos. Drag keeps it all smooth.
      if (nowMs >= this.nextGustAt) {
        this.gustTarget = (Math.random() * 2 - 1) * this.water.chaos * 2.2;
        this.nextGustAt = nowMs + 700 + Math.random() * 1100;
      }
      this.zoneForce += (this.gustTarget - this.zoneForce) * Math.min(1, 2.5 * dt); // ease into the gust
      this.zoneVel += (this.zoneForce + steer * ZONE_PUSH + this.water.dir * this.water.flow * 3) * dt;
      this.zoneVel *= Math.exp(-2.5 * dt); // water drag
      this.zoneX += this.zoneVel * dt;
      const zMin = this.zoneW / 2, zMax = 1 - this.zoneW / 2;
      if (this.zoneX <= zMin || this.zoneX >= zMax) this.zoneVel = 0; // bump the rail, stop dead
      this.zoneX = Math.max(zMin, Math.min(zMax, this.zoneX));

      // THE SUCCESS BAR: rises while the fish is inside the zone, falls while it isn't.
      const inside = Math.abs(this.fishPos - this.zoneX) <= this.zoneW / 2;
      this.bar += (inside ? SUCCESS_GAIN : -SUCCESS_LOSS) * dt;
      this.bar = Math.max(0, Math.min(FISH_CATCH_FULL, this.bar));
      this.draw(nowMs, !inside);

      if (this.bar >= FISH_CATCH_FULL) { const kind = this.fish; this.cancel(); return { type: "caught", kind }; }
      if (this.bar <= 0 || nowMs - this.hookedAt > FIGHT_TIMEOUT_MS) { this.cancel(); return { type: "escaped" }; }
      return null;
    }
    return null;
  }

  dispose(): void {
    window.removeEventListener("resize", this.resize);
    this.overlay.remove();
  }

  private resize = (): void => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(window.innerWidth * dpr);
    this.canvas.height = Math.floor(window.innerHeight * dpr);
  };

  /** Redraw the 2D pole + line + bobber, and (once biting) the catch bar. */
  private draw(nowMs: number, lunging = false, castT: number | null = null): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;
    const W = this.canvas.width, H = this.canvas.height;
    ctx.clearRect(0, 0, W, H);
    const px = (n: number): number => Math.round(n);

    // --- rod: a tapered brown pole from the bottom-right up toward the centre ---
    const buttX = W - px(W * 0.06), buttY = H + px(H * 0.04);
    const tipX = px(W * 0.6), tipY = px(H * (this.phase === "biting" ? 0.25 : 0.29)); // rod held a touch higher
    const seg = 26;
    for (let i = 0; i <= seg; i++) {
      const t = i / seg;
      const x = buttX + (tipX - buttX) * t, y = buttY + (tipY - buttY) * t;
      const w = px((11 - 8 * t) * (W / 1280) * 2 + 2);
      ctx.fillStyle = i % 6 < 3 ? "#a9743c" : "#8a5a30";
      ctx.fillRect(px(x - w / 2), px(y - w / 2), w, w);
    }
    // reel
    ctx.fillStyle = "#3a3d42";
    const rx = buttX + (tipX - buttX) * 0.14, ry = buttY + (tipY - buttY) * 0.14;
    ctx.fillRect(px(rx - 12), px(ry - 12), 24, 24);

    // --- bobber position (over the "water"; landing spot set by the throw power) ---
    const surfaceY = px(H * this.surfFracY);
    let bobX = px(W * this.bobFracX);
    let bobY = surfaceY;
    if (castT !== null) {
      // CASTING: the bobber FLIES from the rod tip out to its landing spot along a lobbed arc,
      // trailing the line behind it; it splashes down in the last stretch.
      const ease = 1 - (1 - castT) * (1 - castT); // decelerating flight
      bobX = px(tipX + (W * this.bobFracX - tipX) * ease);
      const arc = Math.sin(Math.PI * Math.min(1, castT)) * H * (0.10 + 0.10 * this.castPower);
      bobY = px(tipY + (surfaceY - tipY) * ease - arc);
      // slack line trailing the flying bobber (a sagging quadratic)
      ctx.strokeStyle = "rgba(225,232,238,0.85)";
      ctx.lineWidth = Math.max(1, px(W / 900));
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.quadraticCurveTo(px((tipX + bobX) / 2), px(Math.max(tipY, bobY) + H * 0.05 * (1 - castT)), bobX, bobY);
      ctx.stroke();
      if (castT > 0.82) {
        // splashdown ring
        const st = (castT - 0.82) / 0.18;
        ctx.strokeStyle = `rgba(200,225,240,${0.7 * (1 - st)})`;
        ctx.lineWidth = Math.max(1, px(W / 1000));
        const rr = px((6 + st * 26) * (W / 1280) + 4);
        ctx.beginPath(); ctx.ellipse(px(W * this.bobFracX), surfaceY + px(6), rr, rr * 0.4, 0, 0, Math.PI * 2); ctx.stroke();
      }
    } else {
      if (this.phase === "waiting") bobY = surfaceY + px(Math.sin(nowMs / 600) * 4 + 2);
      else bobY = surfaceY + px(10 + (lunging ? 10 : 0) + Math.sin(nowMs / 90) * (lunging ? 4 : 2));

      // --- line: rod tip → bobber ---
      ctx.strokeStyle = "rgba(225,232,238,0.85)";
      ctx.lineWidth = Math.max(1, px(W / 900));
      ctx.beginPath(); ctx.moveTo(tipX, tipY); ctx.lineTo(bobX, bobY); ctx.stroke();
    }

    // --- bobber: red/white float ---
    const br = px(9 * (W / 1280) + 5);
    ctx.fillStyle = "#d23a3a"; ctx.fillRect(bobX - br, bobY - br, br * 2, br);
    ctx.fillStyle = "#f2f2f5"; ctx.fillRect(bobX - br, bobY, br * 2, br);
    ctx.fillStyle = "#1a1a1f"; ctx.fillRect(bobX - 2, bobY - br - px(6), 4, px(6)); // antenna
    // ripple rings while waiting
    if (this.phase === "waiting") {
      ctx.strokeStyle = "rgba(180,210,225,0.35)"; ctx.lineWidth = Math.max(1, px(W / 1100));
      const rr = (br * 1.6) + ((nowMs / 12) % (br * 2.2));
      ctx.beginPath(); ctx.ellipse(bobX, surfaceY + px(6), rr, rr * 0.4, 0, 0, Math.PI * 2); ctx.stroke();
    }

    // --- the CATCH RADAR (only while a fish is hooked): a horizontal track mid-screen with the
    // darting fish icon + the water-drifting green catch zone, and a vertical SUCCESS bar beside
    // it (starts mid — all the way up = caught, all the way down = it slips away). ---
    if (this.phase === "biting") {
      const outside = lunging; // (param reused: true while the fish is OUTSIDE the zone)
      const tw = px(W * 0.44), th = px(28 * (W / 1280) + 10);
      const tx = px((W - tw) / 2), ty = px(H * 0.78); // near the bottom, just above the key hints
      const pad = Math.max(2, px(W / 700));
      // track
      ctx.fillStyle = "#05080a"; ctx.fillRect(tx - pad, ty - pad, tw + pad * 2, th + pad * 2);
      ctx.fillStyle = "rgba(16,28,36,0.85)"; ctx.fillRect(tx, ty, tw, th);
      // green catch zone (transparent)
      const zw = px(tw * this.zoneW);
      const zx = px(tx + this.zoneX * tw - zw / 2);
      const zu = Math.max(2, px(W / 640)); // chunky pixel border unit
      ctx.fillStyle = "rgba(90,224,140,0.35)"; ctx.fillRect(zx, ty, zw, th);
      ctx.fillStyle = "rgba(120,240,160,0.9)";
      ctx.fillRect(zx, ty, zw, zu); ctx.fillRect(zx, ty + th - zu, zw, zu);           // top + bottom
      ctx.fillRect(zx, ty, zu, th); ctx.fillRect(zx + zw - zu, ty, zu, th);           // sides
      // fish icon — TRUE pixel art: a hand-drawn bitmap stamped in chunky units, mirrored to face
      // its travel direction, flushed red while it's outside the zone. Legend: . empty, D dark
      // outline, B body, G top gleam, W belly, E eye.
      const u = Math.max(2, px(W / 480)); // one fish "pixel"
      const FISH_MAP = [ // 16 x 9 "round koi", drawn right-facing (tail left, head right)
        ".......DDDD.....",
        ".D...DDBGGBDD...",
        ".DD.DBGGGBBBBD..",
        ".DDDBGBBBBBBBBD.",
        ".DDDBBBBBBBWEBD.",
        ".DDDBBWWWWBBBBD.",
        ".DD.DBWWWWWBBD..",
        ".D...DDWWWDD....",
        ".......DDDD.....",
      ];
      const fx = px(tx + this.fishPos * tw), fy = ty + px(th / 2);
      const dirRight = this.fishVel >= 0 ? 1 : -1; // face where it's actually swimming
      const pal: Record<string, string> = outside
        ? { B: "#e0685a", G: "#f0a89a", W: "#c9564a", D: "#5e1f18", E: "#10161c" }
        : { B: "#aebfcd", G: "#e6f0f8", W: "#8496a6", D: "#232c35", E: "#10161c" };
      const cols = FISH_MAP[0].length, rows = FISH_MAP.length;
      for (let ry = 0; ry < rows; ry++) {
        for (let rx = 0; rx < cols; rx++) {
          const c = FISH_MAP[ry][rx];
          if (c === ".") continue;
          const gx = dirRight > 0 ? rx : cols - 1 - rx; // mirror for leftward travel
          ctx.fillStyle = pal[c];
          ctx.fillRect(fx + (gx - cols / 2) * u, fy + (ry - rows / 2) * u, u, u);
        }
      }

      // vertical SUCCESS bar to the right, up at mid-screen (decoupled from the low track)
      const sh = px(H * 0.26), sw = px(16 * (W / 1280) + 6);
      const sx = tx + tw + px(22), sy = px(H * 0.42) - px(sh / 2);
      ctx.fillStyle = "#05080a"; ctx.fillRect(sx - pad, sy - pad, sw + pad * 2, sh + pad * 2);
      ctx.fillStyle = "#13202a"; ctx.fillRect(sx, sy, sw, sh);
      const frac = this.bar / FISH_CATCH_FULL;
      const fh = px(sh * frac);
      ctx.fillStyle = frac > 0.8 ? "#49e06a" : frac > 0.4 ? "#8fd24a" : "#e0b53a";
      ctx.fillRect(sx, sy + sh - fh, sw, fh); // fills upward
      ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.fillRect(sx, sy, sw, px(2));          // "caught" line at the top
      ctx.fillStyle = "rgba(255,255,255,0.25)"; ctx.fillRect(sx, sy + px(sh / 2), sw, px(1)); // the mid start line
    }
  }
}
